import { Link } from "react-router-dom";
import Select from "react-select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CREATED_SUCCESS_MSG,
  ERROR_MSG,
  REQUIRED_FIELD,
  SELECT_PRODUCT_CATEGORY_MSG,
  SELECT_PRODUCT_INTERESTED_MSG,
  VALID_EMAIL_MSG,
  VALID_MOBILE_MSG,
} from "../../../../core/data/constants/constant";
import { toast } from "react-toastify";
import LeadApi from "../api/LeadApi";
import { all_routes } from "../../../router/all_routes";
import { Lead } from "../models/LeadModel";
import { handleOffcanvasAction } from "../../../../utils/helpers/handleOffcanvasAction";
import RenderCompanyList from "../../../../core/common/crmComponents/RenderCompanyList";
import { isSuperAdmin } from "../../../../utils/helpers/helper";
import { useProductsData } from "../../products/hooks/useProductsData";
import { useState } from "react";
import useProductOptions from "../../../../utils/hooks/useProductOptions";

const AddLead = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const queryClient = useQueryClient();

  const mutationAdd = useMutation({
    mutationFn: LeadApi.addLead,
    onSuccess: (response) => {
      toast.success(response?.message || CREATED_SUCCESS_MSG);
      queryClient.invalidateQueries("companies");
      formik.resetForm();
      handleOffcanvasAction("offcanvas_add", "hide");
    },
    onError: (error) => {
      toast.error(error.response?.data?.msg || ERROR_MSG);
    },
  });

  const validationSchema = Yup.object({
    firstName: Yup.string().required(REQUIRED_FIELD),
    lastName: Yup.string().required(REQUIRED_FIELD),
    email: Yup.string().email(VALID_EMAIL_MSG).required(REQUIRED_FIELD),
    mobile: Yup.string()
      .matches(
        /^(?:\d{10}|\+\d{1,4}\d{10})$/,
        "Mobile number must be either 10 digits or a valid international format (e.g., +914564566789)."
      )
      .required(REQUIRED_FIELD),
    alternateNumber: Yup.string().matches(
      /^(?:\d{10}|\+\d{1,4}\d{10})$/,
      "Mobile number must be either 10 digits or a valid international format (e.g., +914564566789)."
    ),
    description: Yup.string(),
    probability: Yup.string(),
    ofCompany: isSuperAdmin(user)
      ? Yup.string().required(REQUIRED_FIELD)
      : Yup.mixed().notRequired(),
    productCategory: Yup.string(),
    productInterested: Yup.object()
      .shape({
        name: Yup.string().required(SELECT_PRODUCT_INTERESTED_MSG),
        id: Yup.number(),
        _id: Yup.string(),
      })
      .required(REQUIRED_FIELD),
    mannualForm: Yup.string(),
    address: Yup.object({
      street: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zipcode: Yup.string(),
    }),
    leadSource: Yup.string(),
    referenceSource: Yup.string().required(REQUIRED_FIELD),
    clientCode: Yup.string(),
    language: Yup.string().required(REQUIRED_FIELD),
    occupation: Yup.string().required(REQUIRED_FIELD),
    city: Yup.string().required(REQUIRED_FIELD),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: new Lead(),
    validationSchema,
    onSubmit: (values) => {
      mutationAdd.mutate(values); // Create lead
    },
  });

  const referenceSourceOptions = [
    "Google",
    "Facebook",
    "YouTube",
    "Instagram",
    "WhatsApp",
    "Radio",
    "LinkedIn",
    "News",
    "Website",
    "Referral",
    "Employee Referral",
    "Other",
  ];
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [customOption, setCustomOption] = useState(""); // Stores user-entered "Other" value

  const [isOtherOccupationSelected, setIsOtherOccupationSelected] =
    useState(false);
  const [customOccupation, setCustomOccupation] = useState("");

  const { productCategories, productNamesByCategory } = useProductOptions(
    formik?.values?.ofCompany
  );

  return (
    <>
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add Lead</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={formik.handleSubmit}>
            <div className="accordion" id="main_accordion">
              {/* Basic Info */}
              <div className="accordion-item rounded mb-3">
                <div
                  className={`accordion-header ${
                    (formik.errors.firstName && formik.touched.firstName) ||
                    (formik.errors.lastName && formik.touched.lastName) ||
                    (formik.errors.email && formik.touched.email) ||
                    (formik.errors.mobile && formik.touched.mobile) ||
                    (formik.errors.city && formik.touched.city)
                      ? "bg-danger"
                      : ""
                  }`}
                >
                  <Link
                    to="#"
                    className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                    data-bs-toggle="collapse"
                    data-bs-target="#basic"
                  >
                    <span className="avatar avatar-md rounded text-dark border me-2">
                      <i className="ti ti-user-plus fs-20" />
                    </span>
                    Basic Info
                  </Link>
                </div>
                <div
                  className="accordion-collapse collapse show"
                  id="basic"
                  data-bs-parent="#main_accordion"
                >
                  <div className="accordion-body border-top">
                    <div className="row">
                      {/* First Name */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            First Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="firstName"
                            placeholder="John"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.firstName &&
                            formik.errors.firstName && (
                              <div className="text-danger">
                                {formik.errors.firstName}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Last Name */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Last Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="lastName"
                            placeholder="Doe"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.lastName &&
                            formik.errors.lastName && (
                              <div className="text-danger">
                                {formik.errors.lastName}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Email */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Email<span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            placeholder="john@gmail.com"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <div className="text-danger">
                              {formik.errors.email}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Phone<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="mobile"
                            placeholder="1234567890"
                            value={formik.values.mobile}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.mobile && formik.errors.mobile && (
                            <div className="text-danger">
                              {formik.errors.mobile}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* leadsource*/}
                      {/* <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Lead Source</label>
                          <input
                            type="text"
                            name="leadSource"
                            className="form-control"
                            value={formik.values.leadSource}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.leadSource &&
                          formik.errors.leadSource ? (
                            <div className="text-danger">
                              {formik.errors.leadSource}
                            </div>
                          ) : null}
                        </div>
                      </div> */}

                      {/* probability */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Probability</label>
                          <select
                            name="probability"
                            className="form-select"
                            value={formik.values.probability}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Probability</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                          {formik.touched.probability &&
                          formik.errors.probability ? (
                            <div className="text-danger">
                              {formik.errors.probability}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* alternateNumber */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Alternate Phone Number
                          </label>
                          <input
                            type="text"
                            name="alternateNumber"
                            className="form-control"
                            value={formik.values.alternateNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.alternateNumber &&
                          formik.errors?.alternateNumber ? (
                            <div className="text-danger">
                              {formik.errors?.alternateNumber}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Company List */}
                      <RenderCompanyList
                        formik={formik}
                        user={user}
                        colClass="col-md-6"
                      />

                      {/* Product Category Select Box */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Category
                            <span className="text-danger">*</span>
                          </label>
                          <Select
                            className="select2"
                            classNamePrefix="react-select"
                            options={productCategories} // Options for product categories
                            placeholder="Choose a product category"
                            name="productCategory"
                            value={
                              productCategories?.find(
                                (option) =>
                                  option.label === formik.values.productCategory
                              ) || null
                            }
                            onChange={(selectedOption) => {
                              formik.setFieldValue(
                                "productCategory",
                                selectedOption?.label
                              );
                              formik.setFieldValue("productInterested", null); // Reset productInterested on category change
                            }}
                            onBlur={() =>
                              formik.setFieldTouched("productCategory", true)
                            }
                          />
                          {formik.touched.productCategory &&
                          formik.errors.productCategory ? (
                            <div className="text-danger">
                              {formik.errors.productCategory &&
                                SELECT_PRODUCT_CATEGORY_MSG}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Product Name Select Box */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Name<span className="text-danger">*</span>
                          </label>
                          <Select
                            className="select2"
                            classNamePrefix="react-select"
                            options={
                              (productNamesByCategory &&
                              formik?.values?.productCategory
                                ? productNamesByCategory[
                                    formik.values.productCategory
                                  ]
                                : []) || []
                            }
                            placeholder="Choose a product name"
                            name="productInterested"
                            value={formik.values.productInterested || null} // Pass the full object to Select
                            onChange={(selectedOption) =>
                              formik.setFieldValue(
                                "productInterested",
                                selectedOption
                              )
                            } // Save the full object on selection
                            onBlur={() =>
                              formik.setFieldTouched("productInterested", true)
                            }
                            getOptionLabel={(option) => option.name} // Show product name in dropdown
                            getOptionValue={(option) => option._id} // Ensure unique value is used internally
                          />

                          {/* Show validation error */}
                          {formik.touched.productInterested &&
                          formik.errors.productInterested ? (
                            <div className="text-danger">
                              {formik.errors.productInterested.name}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Client code */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Client Code </label>
                          <input
                            type="text"
                            name="clientCode"
                            className="form-control"
                            value={formik.values.clientCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.clientCode &&
                          formik.errors?.clientCode ? (
                            <div className="text-danger">
                              {formik.errors?.clientCode}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* city */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            City<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="city"
                            className="form-control text-capitalize"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.city && formik.errors?.city ? (
                            <div className="text-danger">
                              {formik.errors?.city}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Preferred Language */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Preferred Language
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="language"
                            className="form-select"
                            value={formik.values.language}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Language</option>
                            <option value="Marathi">Marathi</option>
                            <option value="Hindi">Hindi</option>
                            <option value="Engilsh">English</option>
                          </select>
                          {formik.touched.language && formik.errors.language ? (
                            <div className="text-danger">
                              {formik.errors.language}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Occupation */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Occupation <span className="text-danger">*</span>
                          </label>
                          <select
                            name="occupation"
                            className="form-select text-capitalize"
                            value={formik.values.occupation}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (selectedValue === "Other") {
                                setIsOtherOccupationSelected(true);
                                formik.setFieldValue("occupation", ""); // Clear value
                              } else {
                                setIsOtherOccupationSelected(false);
                                formik.setFieldValue(
                                  "occupation",
                                  selectedValue
                                );
                                setCustomOccupation(""); // Reset custom value
                              }
                            }}
                            onBlur={formik.handleBlur}
                          >
                            <option
                              value=""
                              label={
                                isOtherOccupationSelected
                                  ? "Enter your occupation in Other Field"
                                  : "Select an occupation"
                              }
                            />
                            {[
                              "Job",
                              "Business",
                              "Student",
                              "Housewife",
                              "Retired Person",
                              "Farmer",
                              "Other",
                            ].map((option, index) => (
                              <option key={index} value={option}>
                                {option}
                              </option>
                            ))}
                            {customOccupation && (
                              <option value={customOccupation}>
                                {customOccupation}
                              </option>
                            )}
                          </select>
                          {formik.touched?.occupation &&
                          formik.errors?.occupation ? (
                            <div className="text-danger">
                              {formik.errors.occupation}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Other Input Field for Occupation */}
                      {isOtherOccupationSelected && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="col-form-label">Other</label>
                            <input
                              type="text"
                              name="otherOccupation"
                              className="form-control text-capitalize"
                              value={customOccupation}
                              onChange={(e) =>
                                setCustomOccupation(e.target.value)
                              }
                              onBlur={() => {
                                if (customOccupation.trim()) {
                                  formik.setFieldValue(
                                    "occupation",
                                    customOccupation.trim()
                                  );
                                  setIsOtherOccupationSelected(false);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  if (customOccupation.trim()) {
                                    formik.setFieldValue(
                                      "occupation",
                                      customOccupation.trim()
                                    );
                                    setIsOtherOccupationSelected(false);
                                  }
                                }
                              }}
                              placeholder="Enter other occupation"
                            />
                          </div>
                        </div>
                      )}

                      {/* description */}
                      <div className="col-md-12">
                        <div className="mb-0">
                          <label className="col-form-label">Description</label>
                          <textarea
                            className="form-control"
                            name="description"
                            rows={5}
                            placeholder="Enter additional details or notes here..."
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        {formik.touched.description &&
                        formik.errors.description ? (
                          <div className="text-danger">
                            {formik.errors.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Basic Info */}

              {/* Marketing Attributes */}
              <div className="accordion-item border-top rounded mb-3">
                <div
                  className={`accordion-header ${
                    formik?.errors?.referenceSource &&
                    formik?.touched?.referenceSource
                      ? "bg-danger"
                      : ""
                  }`}
                >
                  <Link
                    to="#"
                    className="accordion-button accordion-custom-button rounded bg-white fw-medium text-dark"
                    data-bs-toggle="collapse"
                    data-bs-target="#marketing"
                  >
                    <span className="avatar avatar-md rounded text-dark border me-2">
                      <i className="ti ti-map-pin-cog fs-20" />
                    </span>
                    Marketing Attributes
                  </Link>
                </div>
                <div
                  className="accordion-collapse collapse"
                  id="marketing"
                  data-bs-parent="#main_accordion"
                >
                  <div className="accordion-body border-top">
                    <div className="row">
                      {/* Lead Source */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Lead Source</label>
                          <div className="input-group">
                            <input
                              className="form-control text-capitalize"
                              name="leadSource"
                              value={formik?.values?.leadSource || ""}
                              disabled
                            />
                            <div className="input-group-text">
                              <i className="ti ti-lock" />
                            </div>
                          </div>
                          {formik.touched.leadSource &&
                          formik.errors.leadSource ? (
                            <div className="text-danger">
                              {formik.errors.leadSource}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Business type */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Business Type
                          </label>
                          <div className="input-group">
                            <input
                              className="form-control text-capitalize"
                              name="businessType"
                              value={formik?.values?.businessType || ""}
                              disabled
                            />
                            <div className="input-group-text">
                              <i className="ti ti-lock" />
                            </div>
                          </div>
                          {formik.touched.businessType &&
                          formik.errors.businessType ? (
                            <div className="text-danger">
                              {formik.errors.businessType}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Reference Source */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Reference Source{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="referenceSource"
                            className="form-select text-capitalize"
                            value={formik.values.referenceSource}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (selectedValue === "Other") {
                                setIsOtherSelected(true);
                                formik.setFieldValue("referenceSource", ""); // Clear value
                              } else {
                                setIsOtherSelected(false);
                                formik.setFieldValue(
                                  "referenceSource",
                                  selectedValue
                                );
                                setCustomOption(""); // Reset custom option
                              }
                            }}
                            onBlur={formik.handleBlur}
                          >
                            <option
                              value=""
                              label={
                                isOtherSelected
                                  ? "Enter your source in Other Field"
                                  : "Select a source"
                              }
                            />
                            {referenceSourceOptions.map((option, index) => (
                              <option key={index} value={option}>
                                {option}
                              </option>
                            ))}
                            {/* Show custom option if added */}
                            {customOption && (
                              <option value={customOption}>
                                {customOption}
                              </option>
                            )}
                          </select>
                          {formik.touched?.referenceSource &&
                          formik.errors?.referenceSource ? (
                            <div className="text-danger">
                              {formik.errors?.referenceSource}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Other Input Field (Visible when "Other" is selected) */}
                      {isOtherSelected && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="col-form-label">Other</label>
                            <input
                              type="text"
                              name="otherReferenceSource"
                              className="form-control text-capitalize"
                              value={customOption}
                              onChange={(e) => setCustomOption(e.target.value)}
                              onBlur={() => {
                                if (customOption.trim()) {
                                  formik.setFieldValue(
                                    "referenceSource",
                                    customOption.trim()
                                  );
                                  setIsOtherSelected(false); // Hide input field
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault(); // Prevent form submission
                                  if (customOption.trim()) {
                                    formik.setFieldValue(
                                      "referenceSource",
                                      customOption.trim()
                                    );
                                    setIsOtherSelected(false); // Hide input field
                                  }
                                }
                              }}
                              placeholder="Enter other reference source"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* /Marketing Attributes */}

              {/* Address Info */}
              <div className="accordion-item border-top rounded mb-3">
                <div className="accordion-header">
                  <Link
                    to="#"
                    className="accordion-button accordion-custom-button rounded bg-white fw-medium text-dark"
                    data-bs-toggle="collapse"
                    data-bs-target="#address"
                  >
                    <span className="avatar avatar-md rounded text-dark border me-2">
                      <i className="ti ti-map-pin-cog fs-20" />
                    </span>
                    Address Info
                  </Link>
                </div>
                <div
                  className="accordion-collapse collapse"
                  id="address"
                  data-bs-parent="#main_accordion"
                >
                  <div className="accordion-body border-top">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Street Address{" "}
                          </label>
                          <input
                            type="text"
                            name="address.street"
                            className="form-control"
                            value={formik.values.address.street}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.address?.street &&
                          formik.errors?.address?.street ? (
                            <div className="text-danger">
                              {formik.errors?.address?.street}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">City </label>
                          <input
                            type="text"
                            name="address.city"
                            className="form-control"
                            value={formik.values.address.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.address?.city &&
                          formik.errors?.address?.city ? (
                            <div className="text-danger">
                              {formik.errors?.address?.city}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            State / Province{" "}
                          </label>
                          <input
                            type="text"
                            name="address.state"
                            className="form-control"
                            value={formik.values.address.state}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.address?.state &&
                          formik.errors?.address?.state ? (
                            <div className="text-danger">
                              {formik.errors?.address?.state}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-0">
                          <label className="col-form-label">Zipcode </label>
                          <input
                            type="text"
                            name="address.zipcode"
                            className="form-control"
                            value={formik.values.address.zipcode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.address?.zipcode &&
                          formik.errors?.address?.zipcode ? (
                            <div className="text-danger">
                              {formik.errors?.address?.zipcode}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Address Info */}
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={mutationAdd.isPending}
              >
                {mutationAdd.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddLead;
